<template>
  <div>
    <div class="echart" v-loading="loading">
      <div class="pieChart">
        <vue-echarts
          v-if="optionData"
          :option="options"
          style="height: 100%;"
          ref="chart"
        />
      </div>
    </div>

    <!-- Bar‐chart Modal -->
    <transition name="fade">
      <div
        v-if="showBarModal"
        class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-8"
        @click.self="closeBarModal"
      >
        <div class="bg-white rounded-2xl p-6 w-full max-w-4xl h-[80vh] relative overflow-hidden">
          <button
            @click="closeBarModal"
            class="absolute top-7 right-9 text-gray-500 hover:text-gray-700 text-4xl z-40"
            aria-label="Close"
          >&times;</button>

          <!-- CompanyBarChart will now render real data -->
          <div style="height:700px;">
            <CompanyBarChart :optionData="barOptionData" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { white } from 'tailwindcss/colors';
import { VueEcharts } from 'vue3-echarts';
import CompanyBarChart from './companyBarChart.vue';

export default {
  name: "PieChart",
  components: {
    VueEcharts,
    CompanyBarChart,
  },
  props: {
    startDate: String,
    endDate: String,
    optionData: Object,
    rawData: Object,
    totalData: Object
  },
  data() {
    return {
      loading: false,
      showBarModal: false,
      // you can build this dynamically based on which legend was clicked
      barOptionData: {
        title: '',
        color: ['#5470C6'],
        data: [],
      },
    };
  },
  computed: {
    options() {
      return {
        color: this.optionData.color,
        title: {
          text: this.optionData.title,
          left: 'left',
          top: '5%',
          padding: [0, 0, 20, 0],
          show: true,
          textStyle: {
            fontSize: 18,
            fontStyle: 'normal',
            fontWeight: 400,
          },
        },
        tooltip: {
          trigger: 'item',
          formatter: '{b} : {c} ({d}%)',
        },
        grid: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          containLabel: true,
        },
        legend: {
          orient: 'vertical',
          left: '60%',
          top: 'center',
          icon: 'square',
          itemWidth: 15,
          textStyle: { fontSize: 12 },
          data: this.optionData.data.map(i => i.name),
          formatter: name => {
            const item = this.optionData.data.find(i => i.name === name);
            if (!item) return name;
            if (name === "Loss") {
              const absVal = Math.abs(item.value).toFixed(3);
              const label = item.value > 0 ? 'Loss' : 'Gain';
              return `${absVal}MT\t\t${label}`;
            }
            return `${item.value}MT\t\t${name}`;
          },
        },
        series: [{
          name: this.optionData.title,
          type: 'pie',
          radius: '60%',
          center: ['35%', '50%'],
          data: this.optionData.data,
          label: {
            show: true,
            position: 'inside',
            formatter: '{d}%',
            color: white,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }],
      };
    }
  },
  mounted() {
    this.$nextTick(() => {
      const chart = this.$refs.chart.getInstance();
      chart.on('legendselectchanged', this.onLegendClick);
    });
  },
  methods: {
    resetPieLegend() {
      // grab the pie chart instance
      const pie = this.$refs.chart.getInstance();
      // re-select every slice by name
      this.optionData.data.forEach(slice => {
        pie.dispatchAction({
          type: 'legendSelect',
          // name must exactly match the legend name
          name: slice.name
        });
      });
    },
    onLegendClick({ name }) {

      console.log('Legend clicked:', this.rawData,name);
      let total = 0;
      const pieTitle = this.optionData.title;
      let companiesArray = [];
      console.log('Pie Title:', pieTitle, 'Name:', name, 'Total:', total);

      if (pieTitle === 'Variance') {
        total = this.totalData.totalQuantityLoaded;
        if (name.startsWith('Loaded')) {
          companiesArray = this.rawData.moreDetails.loadedCompanies || [];
        }
        else if (name.startsWith('Loss')) {
          companiesArray = this.rawData.moreDetails.lossCompanies || [];
        }
      }
      else if (pieTitle === 'Cargo Movement') {
        // Cargo Movement slices are “White Product” / “Black Product”
        if (name === 'White Product') {
          total = this.rawData.moreDetails.whiteOilCompanies.reduce((sum, company) => sum + Number(company.value), 0);
          companiesArray = this.rawData.moreDetails.whiteOilCompanies;
        }
        else if (name === 'Black Product') {
          total = this.rawData.moreDetails.blackOilCompanies.reduce((sum, company) => sum + Number(company.value), 0);
          companiesArray = this.rawData.moreDetails.blackOilCompanies;
        }
      }
      else if (pieTitle === 'White Product') {
        // use `whiteOilCompany` (not whiteProductCompany)
        const entry = (this.rawData.whiteProductCompany || [])
          .find(e => e.product === name);
        companiesArray = entry?.companies || [];
        total = companiesArray.reduce((sum, company) => sum + Number(company.value), 0);
      }
      else if (pieTitle === 'Black Product') {
        // use `blackOilCompany`
        const entry = (this.rawData.blackProductCompany || [])
          .find(e => e.product === name);
        companiesArray = entry?.companies || [];
        total = companiesArray.reduce((sum, company) => sum + Number(company.value), 0);
      }
      // (we skip Cargo Movement here because we don’t actually have a `cargoMovementCompany` array)

      this.barOptionData = {
        title: `${name} Breakdown`,
        data: companiesArray.map(c => ({
          name:  c.companyName,
          value: c.value
        })),
        total,
        color: []  // gradient is applied inside CompanyBarChart
      };
      this.showBarModal = true;
    },
    closeBarModal() {
      this.resetPieLegend();
      this.showBarModal = false;
    }
  }
};
</script>

<style scoped>
.echart {
  width: auto;
  overflow-x: auto;
  scrollbar-width: thin;
}
.pieChart {
  height: 300px;
  width: 600px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .2s;
}

</style>
